/**
 * FieldDetailPopover — popover при клике на поле.
 * Показывает:
 *   - field_number, crop, area, NDVI, CV
 *   - "Открыть AI-агроном" CTA (pre-filled query про это поле)
 *   - "Подробнее" → переход на /fields/:id
 *
 * AK-65: при mode !== 'none' добавляются 2 блока:
 *   1. Cloud coverage badge (из /api/v1/ndvi/field-coverage):
 *        - <30%: зелёный   "Облачность сцены: X%" (иконка Check)
 *        - 30-60%: жёлтый  "Облачность сцены: X% (возможно искажение)"
 *        - >60%: красный   "Облачность сцены: X% (NDVI ненадёжный)"
 *   2. Per-field NDVI stats:
 *        "Среднее по полю: 0.62 ± 0.08 (87% площади clear)"
 *        avg_ndvi = MVT.avg_ndvi
 *        std_ndvi = cv_ndvi * |avg_ndvi|   (MVT отдаёт cv, а не std напрямую)
 *        valid_ratio из /field-coverage
 *
 * Управляется selectedFieldId в useMapStore. При null — popover не виден.
 */
import { useMemo } from 'react';
import { X, Sparkles, ExternalLink, Check, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useMapStore } from '@/lib/map/useMapStore';
import { useCropsReference } from '@/lib/map/useCropsReference';
import { useFieldCoverage } from '@/lib/map/useFieldCoverage';
import { cn } from '@/lib/utils';

export interface FieldDetail {
  id: string;
  field_number?: string | null;
  current_crop_id?: string | null;
  current_phase?: 'planned' | 'growing' | 'harvested' | null;
  area_ha?: number | null;
  avg_ndvi?: number | null;
  cv_ndvi?: number | null;
}

interface FieldDetailPopoverProps {
  /**
   * Сырые properties из MVT click. Если поля нет — null, popover скрыт.
   */
  field: FieldDetail | null;
  /** Координаты для позиционирования (pixel offset от map container). */
  position?: { x: number; y: number };
  onAskAI?: (field: FieldDetail) => void;
  onOpenDetail?: (fieldId: string) => void;
}

const PHASE_LABELS: Record<NonNullable<FieldDetail['current_phase']>, string> = {
  planned: 'Запланирована',
  growing: 'Растёт',
  harvested: 'Убрана',
};

/** Цветовая палитра для cloud-coverage badge (AK-65 spec). */
type CloudTone = 'good' | 'warn' | 'bad';

function classifyCloud(cloudPct: number): CloudTone {
  if (cloudPct < 30) return 'good';
  if (cloudPct <= 60) return 'warn';
  return 'bad';
}

const CLOUD_BADGE_CLASSES: Record<CloudTone, string> = {
  good: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-400 dark:border-green-900',
  warn: 'bg-yellow-50 text-yellow-800 border-yellow-200 dark:bg-yellow-950/40 dark:text-yellow-400 dark:border-yellow-900',
  bad: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-400 dark:border-red-900',
};

const CLOUD_LABELS: Record<CloudTone, (pct: number) => string> = {
  good: (p) => `Облачность сцены: ${p}%`,
  warn: (p) => `Облачность сцены: ${p}% (возможно искажение)`,
  bad: (p) => `Облачность сцены: ${p}% (NDVI ненадёжный)`,
};

/**
 * Иконки вместо эмодзи ✅/⚠/❌: эмодзи рендерятся системным шрифтом, не
 * красятся `currentColor` и в тёмной теме живут своей жизнью — на Win/Mac/
 * Android это буквально три разные картинки (design review 18.08, зона 1 · А9).
 */
const CLOUD_ICONS: Record<CloudTone, typeof Check> = {
  good: Check,
  warn: TriangleAlert,
  bad: TriangleAlert,
};

export function FieldDetailPopover({
  field,
  position,
  onAskAI,
  onOpenDetail,
}: FieldDetailPopoverProps) {
  const setSelectedFieldId = useMapStore((s) => s.setSelectedFieldId);
  const mode = useMapStore((s) => s.mode);
  const selectedDate = useMapStore((s) => s.selectedDate);
  const crops = useCropsReference();

  // Hook вызывается ВСЕГДА (rules-of-hooks), но disable'ится через enabled.
  // Когда field=null или mode='none' — query не fetch'ит и data=undefined.
  const showSatBlocks = mode !== 'none';
  const coverage = useFieldCoverage({
    fieldId: field?.id ?? null,
    sceneDate: selectedDate ?? null,
    enabled: showSatBlocks && Boolean(field?.id),
  });

  // std_ndvi выводим из cv_ndvi: cv = std / |avg|  →  std = cv * |avg|.
  // MVT отдаёт cv напрямую (см. backend/api/v1/tiles.py:160). Если avg=0/null —
  // std не вычислим, скрываем "± std" из строки.
  const stdNdvi = useMemo<number | null>(() => {
    if (field?.avg_ndvi == null || field?.cv_ndvi == null) return null;
    if (field.avg_ndvi === 0) return null;
    return field.cv_ndvi * Math.abs(field.avg_ndvi);
  }, [field?.avg_ndvi, field?.cv_ndvi]);

  if (!field) return null;

  const crop = field.current_crop_id
    ? crops.data?.find((c) => c.id === field.current_crop_id)
    : undefined;

  const ndviTone =
    field.avg_ndvi == null
      ? 'text-muted-foreground'
      : field.avg_ndvi >= 0.6
        ? 'text-ndvi-good'
        : field.avg_ndvi >= 0.4
          ? 'text-ndvi-warning'
          : 'text-ndvi-bad';

  // Видимость блока NDVI-статистики:
  //   - mode != 'none' (юзер смотрит спутник)
  //   - есть avg_ndvi из MVT ИЛИ загружается coverage
  // Silent-fail: если coverage.error — показываем что есть (avg/std из MVT).
  const hasCoverageData = coverage.data?.has_data === true;
  const cloudPct = coverage.data?.cloud_pct ?? null;
  const validRatio = coverage.data?.valid_ratio ?? null;

  const cloudTone: CloudTone | null =
    cloudPct != null ? classifyCloud(cloudPct) : null;

  const showStatsBlock =
    showSatBlocks && (field.avg_ndvi != null || coverage.isLoading);

  return (
    <div
      className={cn(
        'absolute z-20 w-[280px] rounded-xl bg-card border border-border shadow-lg p-3',
        'animate-in fade-in zoom-in-95 duration-150',
      )}
      style={
        position
          ? { left: position.x + 12, top: position.y + 12 }
          : { right: 16, top: 16 }
      }
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="text-sm font-semibold text-foreground">
            Поле {field.field_number ?? '—'}
          </div>
          {crop && (
            <div className="flex items-center gap-2 mt-1">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: crop.color }}
              />
              <span className="text-xs text-muted-foreground">{crop.name}</span>
              {field.current_phase && (
                <Badge variant="secondary" className="text-xs px-2 py-0">
                  {PHASE_LABELS[field.current_phase]}
                </Badge>
              )}
            </div>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 -mr-1 -mt-1"
          onClick={() => setSelectedFieldId(null)}
          aria-label="Закрыть"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-3 text-center">
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">
            Площадь
          </div>
          <div className="text-sm font-mono mt-1">
            {field.area_ha != null ? `${field.area_ha.toFixed(1)} га` : '—'}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">
            NDVI
          </div>
          <div className={cn('text-sm font-mono font-semibold mt-1', ndviTone)}>
            {field.avg_ndvi != null ? field.avg_ndvi.toFixed(2) : '—'}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">
            CV
          </div>
          <div className="text-sm font-mono mt-1">
            {field.cv_ndvi != null ? field.cv_ndvi.toFixed(2) : '—'}
          </div>
        </div>
      </div>

      {/* AK-65: Cloud coverage badge — только при активном NDVI-режиме. */}
      {showSatBlocks && (
        <>
          {coverage.isLoading && (
            <Skeleton className="h-6 w-full mt-3" />
          )}
          {!coverage.isLoading && cloudTone && cloudPct != null && (
            <div
              className={cn(
                'mt-3 flex items-center gap-2 px-2 py-1 rounded-md border text-xs font-medium',
                CLOUD_BADGE_CLASSES[cloudTone],
              )}
              role="status"
            >
              {(() => {
                const CloudIcon = CLOUD_ICONS[cloudTone];
                return <CloudIcon size={14} aria-hidden className="shrink-0" />;
              })()}
              <span>{CLOUD_LABELS[cloudTone](cloudPct)}</span>
            </div>
          )}
        </>
      )}

      {/* AK-65: Per-field NDVI stats. */}
      {showStatsBlock && (
        <div className="mt-2 px-2 py-2 rounded-md bg-muted/40 border border-border/50">
          <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
            Статистика поля
          </div>
          {coverage.isLoading && !hasCoverageData ? (
            <Skeleton className="h-3.5 w-full" />
          ) : (
            <div className="text-xs text-foreground leading-snug">
              {field.avg_ndvi != null ? (
                <>
                  Среднее по полю:{' '}
                  <span className="font-mono font-medium">
                    {field.avg_ndvi.toFixed(2)}
                  </span>
                  {stdNdvi != null && (
                    <>
                      {' '}±{' '}
                      <span className="font-mono">
                        {stdNdvi.toFixed(2)}
                      </span>
                    </>
                  )}
                  {validRatio != null && (
                    <>
                      {' '}
                      <span className="text-muted-foreground">
                        ({Math.round(validRatio * 100)}% площади clear)
                      </span>
                    </>
                  )}
                </>
              ) : (
                <span className="text-muted-foreground">
                  Нет данных NDVI на эту дату
                </span>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col gap-2 mt-3">
        <Button
          type="button"
          variant="default"
          size="sm"
          className="w-full gap-2"
          onClick={() => onAskAI?.(field)}
        >
          <Sparkles className="h-3.5 w-3.5" />
          Спросить AI-агронома
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="w-full gap-2"
          onClick={() => onOpenDetail?.(field.id)}
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Подробнее
        </Button>
      </div>
    </div>
  );
}
